/**
 * Small text checks shared by the plain-language summary and highlights tools: words,
 * sentences, acronyms and in-text citations.
 *
 * They are heuristics and the pages say so. An acronym detector cannot know that "DNA"
 * needs no definition for most readers, and a citation detector cannot always tell a
 * year in parentheses from a reference — so the tools flag, and the writer decides.
 */

const RE_SPACE = /\s+/;
const RE_WORDLIKE = /[\p{L}\p{N}]/u;

/** Tokens with at least one letter or digit — a dash or a stray bullet is not a word. */
export function wordsOf(text: string): string[] {
  return text
    .trim()
    .split(RE_SPACE)
    .filter((token) => RE_WORDLIKE.test(token));
}

export function countWords(text: string): number {
  return wordsOf(text).length;
}

/*
 * A sentence ends at ., ! or ? (plus any closing quote or bracket) followed by
 * whitespace or the end of the text. Requiring the space keeps "2.5 times" in one
 * sentence — a bare `[.!?]` split cuts it in two, and a lay summary is full of numbers
 * like "1.5 times as likely".
 */
const RE_SENTENCE_BOUNDARY = /[.!?]+["”’)\]]*(?=\s|$)/gu;

/** The sentences of a text, in order, trimmed. */
export function splitSentences(text: string): string[] {
  const sentences: string[] = [];
  let start = 0;
  for (const match of text.matchAll(RE_SENTENCE_BOUNDARY)) {
    const end = (match.index ?? 0) + match[0].length;
    sentences.push(text.slice(start, end));
    start = end;
  }
  sentences.push(text.slice(start));
  return sentences
    .map((sentence) => sentence.trim())
    .filter((sentence) => RE_WORDLIKE.test(sentence));
}

/* ------------------------------------------------------------------ acronyms */

const RE_TOKEN = /[\p{L}\p{N}][\p{L}\p{N}-]*[\p{L}\p{N}]/gu;
const RE_UPPER = /\p{Lu}/gu;
const RE_LETTER = /\p{L}/gu;

/** Roman numerals that look like acronyms ("phase II"). */
const ROMAN_NUMERALS = new Set([
  "II",
  "III",
  "IV",
  "VI",
  "VII",
  "VIII",
  "IX",
  "XI",
  "XII",
]);

/**
 * An acronym has at least two capitals, and capitals make up at least half its letters:
 * DNA, RCTs, CO2, COVID-19, mRNA, SARS-CoV-2 — but not McDonald or iPhone.
 */
function isAcronym(token: string): boolean {
  const upper = token.match(RE_UPPER)?.length ?? 0;
  const letters = token.match(RE_LETTER)?.length ?? 0;
  return upper >= 2 && upper * 2 >= letters && !ROMAN_NUMERALS.has(token);
}

/** Distinct acronyms in order of first appearance. */
export function findAcronyms(text: string): string[] {
  const found: string[] = [];
  for (const token of text.match(RE_TOKEN) ?? []) {
    if (isAcronym(token) && !found.includes(token)) {
      found.push(token);
    }
  }
  return found;
}

const RE_PLURAL_ACRONYM = /^(\p{Lu}[\p{Lu}\p{N}-]*)s$/u;

/**
 * Whether the text spells the acronym out itself — "polymerase chain reaction (PCR)"
 * or "PCR (polymerase chain reaction)". A plural ("RCTs") counts as defined when its
 * singular is.
 */
export function definesAcronym(text: string, acronym: string): boolean {
  const singular = RE_PLURAL_ACRONYM.exec(acronym)?.[1];
  const forms = singular ? [acronym, singular] : [acronym];
  return forms.some(
    (form) => text.includes(`(${form})`) || text.includes(`${form} (`)
  );
}

/* ----------------------------------------------------------------- citations */

/** "[3]", "[1, 2]", "[4–6]" */
const RE_NUMERIC_CITATION = /\[\s*\d+(?:\s*[,–-]\s*\d+)*\s*\]/gu;
/**
 * A parenthesis that ends in a year: "(Smith, 2020)", "(Lee et al., 2019a)", "(2021)".
 * A year that closes a range — "(1990–2020)" — is a period, not a citation.
 */
const RE_AUTHOR_YEAR = /\([^()]*?(?<![\d–-])\b(?:1[6-9]|20)\d{2}[a-z]?\)/gu;
const RE_ET_AL = /\bet al\.?/giu;

/** Reference markers that mean nothing away from the reference list. */
export function findCitations(text: string): string[] {
  const found: string[] = [];
  for (const pattern of [RE_NUMERIC_CITATION, RE_AUTHOR_YEAR, RE_ET_AL]) {
    for (const match of text.matchAll(pattern)) {
      if (!found.includes(match[0])) {
        found.push(match[0]);
      }
    }
  }
  return found;
}
